import { isEmpty } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectIsGlobalSmsFirstPartyUIFeatureFlagOn } from 'core/userContext';
import { useQueryVerifiedContactCardByProjectId } from 'modules/contactCards/hooks/queries';
import { ContactCard } from 'modules/contactCards/types';
import { useQuerySmsPreferencesForPhoneNumber } from 'modules/globalSms/hooks';
import { OptOutType } from 'modules/globalSms/types';
import { PhoneNumberPreferences, SMSContentDefinition } from 'modules/graphql';
import { useGetTemplateUserJsonTestData } from 'modules/templateEditor/hooks/useGetTemplateUserJsonTestData';

import { PreviewWithDataErrorMessage, TemplateEditViews } from '../../common/types';
import {
  getContactCardContentData,
  getContentWithData,
  getPreviewWithDataError,
} from '../../common/utils';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { usePreviewWithDataContext } from '../../context/PreviewWithDataContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { getFullMessage, tryParseJSONObject } from '../utils';
import { SMSPreviewContext, SMSPreviewContextState } from './SMSPreviewContext';

export const SMSPreviewContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { liveTemplate } = useEditableFieldsContext();
  const { canRefreshPreview, dataFeedData, userData, isUserDataDirty, setIsUserDataDirty } =
    usePreviewWithDataContext();
  const { data: contactCards = [] } = useQueryVerifiedContactCardByProjectId();
  const { templateId, view } = useQueryParamContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messageContent, setMessageContent] = useState<string>('');
  const [previewError, setPreviewError] = useState<PreviewWithDataErrorMessage | undefined>(
    undefined,
  );
  const [optInMessageIsShown, setOptInMessageIsShown] = useState(true);
  const [optOutType, setOptOutType] = useState<OptOutType>(OptOutType.ALPHANUMERIC);
  const isPreviewWithData = view === TemplateEditViews.preview;

  const isGlobalSmsEnabled = useSelector(selectIsGlobalSmsFirstPartyUIFeatureFlagOn);
  const userDataJson: Record<string, any> = tryParseJSONObject(userData) || {}; // we could probably remove this default but I just want to be safe for now, we will refactor to a custom hook later.
  const userPhoneNumber: string = 'phoneNumber' in userDataJson ? userDataJson.phoneNumber : '';
  const { results: userOptOutPreferences = {} as PhoneNumberPreferences } =
    useQuerySmsPreferencesForPhoneNumber(userPhoneNumber);
  const hasAlphanumericSupport =
    'alphanumericOptOut' in userOptOutPreferences &&
    userOptOutPreferences.alphanumericOptOut !== null;

  const smsData = (liveTemplate?.mediumData || {}) as SMSContentDefinition;
  const hasContent = !isEmpty(smsData);
  const locale = smsData?.locale?.name || '';
  const optOutDisclaimer =
    (hasAlphanumericSupport && isGlobalSmsEnabled
      ? userOptOutPreferences[`${optOutType}OptOut` as keyof PhoneNumberPreferences]
      : smsData.optOutDisclaimer?.message) || '';
  const fullSmsMessage = useMemo(
    () =>
      optInMessageIsShown
        ? getFullMessage(smsData.message || '', optOutDisclaimer)
        : smsData.message || '',
    [smsData?.message, optOutDisclaimer, optInMessageIsShown],
  );

  const [contactCard, setContactCard] = useState<ContactCard | undefined>(
    contactCards.find((card: ContactCard) => card.id === smsData.verifiedContactCardId),
  );
  const userCountry: string = 'country' in userDataJson ? userDataJson.country : '';
  const isUnitedStatesOrCanada = userCountry === 'United States' || userCountry === 'Canada';
  const shouldShowContactCard =
    !!contactCard && (!isPreviewWithData || !userCountry.length || isUnitedStatesOrCanada);

  /**
   * We need a separation between the actual contact card and the preview here.
   * Since we check the ContactCard values for the handlebar regexs, we cannot overwrite them.
   * Otherwise, subsequent calls to the "getContactCardContentData" function do not work since the handlebar now has the preview string and not the handlebar value.
   */
  const [previewContactCard, setPreviewContactCard] = useState(contactCard);

  useEffect(() => {
    setContactCard(
      contactCards.find((card: ContactCard) => card.id === smsData.verifiedContactCardId),
    );
    setPreviewContactCard(
      contactCards.find((card: ContactCard) => card.id === smsData.verifiedContactCardId),
    );
  }, [contactCards, smsData.verifiedContactCardId]);

  const { getTemplateUserJsonTestData } = useGetTemplateUserJsonTestData();

  const onRefreshPreview = useCallback(async () => {
    // Only make request when template data has been received
    if (canRefreshPreview && hasContent) {
      setIsLoading(true);

      const userDataToSave = await getTemplateUserJsonTestData(userData, isUserDataDirty);

      getContentWithData(
        templateId,
        {
          subject: fullSmsMessage,
        },
        JSON.parse(dataFeedData),
        JSON.parse(userDataToSave),
      )
        .then(({ data }) => {
          setIsUserDataDirty(false);
          setMessageContent(data.subject);
          setPreviewError(undefined);
        })
        .catch(({ response }) => {
          setPreviewError(getPreviewWithDataError(response?.data?.message || ''));
        })
        .finally(() => {
          setIsLoading(false);
        });
      setPreviewContactCard(
        shouldShowContactCard
          ? {
              ...contactCard,
              ...getContactCardContentData(contactCard, JSON.parse(userData)),
            }
          : undefined,
      );
    }
  }, [
    canRefreshPreview,
    contactCard,
    dataFeedData,
    fullSmsMessage,
    hasContent,
    shouldShowContactCard,
    templateId,
    userData,
    isUserDataDirty,
    setIsUserDataDirty,
    getTemplateUserJsonTestData,
  ]);

  /* Note: we only want this useEffect to run when dataFeedData or userData change even though it is
   * also dependent on onRefreshPreview. However, onRefreshPreview shares ALL of the useEffect's
   * dependencies, so the version of onRefreshPreview used will always be current.
   * Including onRefreshPreview as a dependency here would cause an unintended side-effect of
   * running onRefreshPreview on each change of fullSmsMessage */
  useEffect(() => {
    if (canRefreshPreview) {
      onRefreshPreview();
    }
  }, [canRefreshPreview, dataFeedData, locale, optOutType, userData, optInMessageIsShown]);

  const context: SMSPreviewContextState = {
    hasAlphanumericSupport,
    isLoading,
    messageContent,
    onRefreshPreview,
    optOutType,
    previewContactCard,
    previewError,
    setOptOutType,
    optInMessageIsShown,
    setOptInMessageIsShown,
    fullSmsMessage,
  };

  return <SMSPreviewContext.Provider value={context}>{children}</SMSPreviewContext.Provider>;
};
